import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { MpkPdfData } from "../../types/CityObjectPdfData";
import { formatFinnishDate, formatNumber, formatCurrency } from "../../utilities/PdfUtils";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 9, fontFamily: "Helvetica" },
  title: { fontSize: 14, fontWeight: "bold", marginBottom: 2 },
  subtitle: { fontSize: 11, fontWeight: "demibold", marginBottom: 4 },
  signed: { fontSize: 9, fontWeight: "bold", color: "#1b5e20", marginBottom: 4 },
  divider: { borderBottomWidth: 1, borderBottomColor: "#999", marginBottom: 10 },
  row: { flexDirection: "row", marginBottom: 3 },
  labelCell: { width: "15%", fontWeight: "bold" },
  valueCell: { width: "35%" },
  sectionTitle: { fontSize: 11, fontWeight: "bold", marginTop: 12, marginBottom: 6 },
  tableHeader: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#ccc", paddingBottom: 2, marginBottom: 4 },
  tableRow: { flexDirection: "row", marginBottom: 2 },
  totalRow: { flexDirection: "row", marginTop: 4, borderTopWidth: 1, borderTopColor: "#ccc", paddingTop: 4 },
  colName: { width: "30%" },
  colUnit: { width: "10%" },
  colNum: { width: "10%", textAlign: "right" },
  bold: { fontWeight: "bold" },
  sigSection: { marginTop: 16 },
  sigRow: { flexDirection: "row", marginBottom: 3 },
  sigLabel: { width: "25%", fontWeight: "bold" },
  sigValue: { width: "75%" },
  notSigned: { fontStyle: "italic", color: "#e65100", marginTop: 16 },
  footer: { position: "absolute", bottom: 20, left: 30, right: 30, textAlign: "center", fontSize: 8, color: "#666" },
});

interface Props {
  data: MpkPdfData;
}

const InfoRow = ({ l1, v1, l2, v2 }: { l1: string; v1: string; l2: string; v2: string }) => (
  <View style={styles.row}>
    <Text style={styles.labelCell}>{l1}</Text>
    <Text style={styles.valueCell}>{v1}</Text>
    <Text style={styles.labelCell}>{l2}</Text>
    <Text style={styles.valueCell}>{v2}</Text>
  </View>
);

const MpkPDF = ({ data }: Props) => {
  const grandTotal = data.workSteps.reduce((sum, ws) => sum + (ws.totalPrice ?? 0), 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Measurement Protocol (MPK) — #{data.perceptionId}</Text>
        <Text style={styles.subtitle}>{data.location}</Text>
        {data.isSigned && <Text style={styles.signed}>SIGNED</Text>}
        <View style={styles.divider} />

        <InfoRow l1="Contract" v1={data.contractName} l2="Contractor" v2={data.contractorName ?? "—"} />
        <InfoRow l1="Status" v1={data.statusLabel} l2="Foreman" v2={data.foremanName ?? "—"} />
        <InfoRow l1="Subscriber" v1={data.subscriberName ?? "—"} l2="Order no." v2={data.orderNumber ?? "—"} />
        <InfoRow l1="Planned date" v1={formatFinnishDate(data.plannedDate)} l2="Meas. date" v2={formatFinnishDate(data.measurementDate)} />
        <InfoRow l1="Meas. by" v1={data.measurementPersons ?? "—"} l2="Length (m)" v2={data.objectLength?.toFixed(1) ?? "—"} />
        <InfoRow l1="Width (m)" v1={data.objectWidth?.toFixed(1) ?? "—"} l2="" v2="" />

        {data.workSteps.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Work Items</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.colName, styles.bold]}>Item</Text>
              <Text style={[styles.colUnit, styles.bold]}>Unit</Text>
              <Text style={[styles.colNum, styles.bold]}>Qty</Text>
              <Text style={[styles.colNum, styles.bold]}>Area</Text>
              <Text style={[styles.colNum, styles.bold]}>Weight</Text>
              <Text style={[styles.colNum, styles.bold]}>Unit Price</Text>
              <Text style={[styles.colNum, styles.bold]}>Total</Text>
            </View>
            {data.workSteps.map((ws, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={styles.colName}>{ws.itemName}</Text>
                <Text style={styles.colUnit}>{ws.unitName ?? ""}</Text>
                <Text style={styles.colNum}>{formatNumber(ws.quantity)}</Text>
                <Text style={styles.colNum}>{formatNumber(ws.area)}</Text>
                <Text style={styles.colNum}>{formatNumber(ws.weight)}</Text>
                <Text style={styles.colNum}>{formatNumber(ws.unitPrice)}</Text>
                <Text style={styles.colNum}>{formatNumber(ws.totalPrice)}</Text>
              </View>
            ))}
            <View style={styles.totalRow}>
              <Text style={[styles.colName, styles.bold]}>TOTAL</Text>
              <Text style={styles.colUnit} />
              <Text style={styles.colNum} />
              <Text style={styles.colNum} />
              <Text style={styles.colNum} />
              <Text style={styles.colNum} />
              <Text style={[styles.colNum, styles.bold]}>{formatCurrency(grandTotal)}</Text>
            </View>
          </View>
        )}

        {data.isSigned ? (
          <View style={styles.sigSection}>
            <Text style={styles.sectionTitle}>Signatures</Text>
            <View style={styles.sigRow}>
              <Text style={styles.sigLabel}>Primary signer</Text>
              <Text style={styles.sigValue}>{data.primarySigner ?? "—"}</Text>
            </View>
            <View style={styles.sigRow}>
              <Text style={styles.sigLabel}>Secondary signer</Text>
              <Text style={styles.sigValue}>{data.secondarySigner ?? "—"}</Text>
            </View>
            <View style={styles.sigRow}>
              <Text style={styles.sigLabel}>Signed date</Text>
              <Text style={styles.sigValue}>{formatFinnishDate(data.signedDate)}</Text>
            </View>
          </View>
        ) : (
          <Text style={styles.notSigned}>Not yet signed</Text>
        )}

        <Text style={styles.footer} render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
};

export default MpkPDF;
